import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextButton } from '../../../global/components/Button/Button';
import TextInput from '../../../global/components/TextField/TextField';
import ColumnCard from '../../../global/layouts/ColumnCard/ColumnCard';
import commonContent from '../common/content';
import { FormSection, NextSteps, StyledButton } from '../styles';

import content from './content';

import type { User } from '../../../../types/user';
import type { Control, FieldValues } from 'react-hook-form';

type StringCertification = {
    title: string;
    institution: string;
    endDate: string;
};

const formSchema = yup.object().shape({
    certification: yup.array().of(
        yup.object().shape({
            title: yup.string(),
            institution: yup.string(),
            endDate: yup.string()
        })
    )
});

const convertLuxonToString = (data: User['certification']) =>
    data.map((certification) => ({
        ...certification,
        endDate: certification.endDate.toFormat('yyyy-MM-dd')
    }));

const convertStringToLuxon = (data: StringCertification[]) =>
    data.map((certification) => ({
        ...certification,
        endDate: DateTime.fromFormat(certification.endDate, 'yyyy-MM-dd')
    }));

export default function CertificationSlide(props: {
    profile: User['certification'];
    updateProfile: (value: User['certification']) => void;
    prevURL: string;
    onClose: () => Promise<void>;
    isEdit?: boolean;
}) {
    const {
        handleSubmit,
        formState: { errors },
        control
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: { certification: convertLuxonToString(props.profile) }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'certification'
    });

    const onSubmit = (data: { certification: StringCertification[] }) => {
        props.updateProfile(convertStringToLuxon(data.certification));
    };

    return (
        <ColumnCard
            closeScreen
            step={3}
            screenType={'candidate'}
            onClose={props.onClose}
            id={'certificationSlide'}
            wide
            hideImage
        >
            <FormSection onSubmit={handleSubmit(onSubmit)}>
                <Typography
                    variant='h2'
                    component='h1'
                    fontWeight='400'
                    sx={{ mb: 2 }}
                >
                    {commonContent.common.title}
                </Typography>
                <Typography>
                    {
                        'Tell us about any certifications or trainings you have received in the past.'
                    }
                </Typography>
                {fields.map((field, index) => (
                    <div key={field.id} style={{ display: 'flex' }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: '1px solid #C2C2C2',
                                padding: '1rem',
                                borderRadius: '0.5rem',
                                marginTop: '1rem',
                                flex: 1
                            }}
                        >
                            <TextInput
                                id={`certification.${index}.title`}
                                label={content.certification.title.label}
                                color='primary'
                                error={!!errors.certification?.[index]?.title}
                                errorText={
                                    errors.certification?.[index]?.title
                                        ?.message
                                }
                                placeholder={
                                    content.certification.title.placeholder
                                }
                                control={
                                    control as unknown as Control<FieldValues>
                                }
                            />
                            <TextInput
                                id={`certification.${index}.institution`}
                                label={content.certification.institution.label}
                                color='primary'
                                error={
                                    !!errors.certification?.[index]?.institution
                                }
                                errorText={
                                    errors.certification?.[index]?.institution
                                        ?.message
                                }
                                placeholder={
                                    content.certification.institution
                                        .placeholder
                                }
                                control={
                                    control as unknown as Control<FieldValues>
                                }
                            />
                            <Controller
                                name={`certification.${index}.endDate`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <LocalizationProvider
                                        dateAdapter={AdapterLuxon}
                                    >
                                        <DatePicker
                                            label='End date'
                                            format={'YYYY-MM-DD'}
                                            value={DateTime.fromFormat(
                                                value,
                                                'yyyy-MM-dd'
                                            )}
                                            onChange={(newValue) =>
                                                onChange(
                                                    newValue?.toFormat(
                                                        'yyyy-MM-dd'
                                                    )
                                                )
                                            }
                                            sx={{ flex: 1, mt: 2 }}
                                        />
                                    </LocalizationProvider>
                                )}
                            />
                        </div>
                        {fields.length > 1 && (
                            <IconButton
                                id={`certification.${index}.remove`}
                                color='primary'
                                aria-label={content.certification.remove}
                                component='label'
                                sx={{ ml: 1 }}
                                onClick={() => remove(index)}
                            >
                                <HighlightOffIcon />
                            </IconButton>
                        )}
                    </div>
                ))}
                <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                    <TextButton
                        id={'addCertification'}
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ fontWeight: 400, mt: 2 }}
                        onClick={() =>
                            append({
                                endDate: '',
                                title: '',
                                institution: ''
                            })
                        }
                    >
                        {content.certification.add}
                    </TextButton>
                </div>
                <NextSteps>
                    {!props.isEdit && (
                        <TextButton to={props.prevURL}>
                            {commonContent.common.back}
                        </TextButton>
                    )}
                    <StyledButton
                        variant='outlined'
                        sx={{ m: 2 }}
                        submit
                        id='submit'
                    >
                        {props.isEdit ? 'Save' : commonContent.common.submit}
                    </StyledButton>
                </NextSteps>
            </FormSection>
        </ColumnCard>
    );
}
